/**
 * proto google.type.Decimal と JavaScript number の相互変換 helper。
 *
 * proto Decimal は精度保持のため value を string で持つ (decimal_pb)。
 * 表示・計算境界で number 化する際の「未設定 / 空文字 / 非数値」の畳み方が
 * page ごとに重複実装されていたため (= SupplyBilateral / SupplyGap)、ここに集約する。
 *
 * 「値なし」を区別したい箇所 (= UR バッジの 0-1 比較など) は `decimalToNumber`、
 * 「値なし」を 0 とみなしてよい箇所 (= チャート値 / CSV / toFixed) は `decimalToNumberOr`
 * を使う。
 */
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

/**
 * proto google.type.Decimal を JavaScript number に変換する。
 * 未設定 (= undefined) / 空文字 / 非数値は **undefined** を返し、「値なし」と 0 を区別する。
 */
export function decimalToNumber(d: Decimal | undefined): number | undefined {
  if (d === undefined || d.value === '') return undefined
  const n = Number.parseFloat(d.value)
  return Number.isFinite(n) ? n : undefined
}

/**
 * proto google.type.Decimal を JavaScript number に変換し、
 * 未設定 / 空文字 / 非数値は fallback (既定 0) を返す。
 * 「値なし」を数値 0 とみなしてよい箇所 (= チャート値 / CSV / toFixed) で使う。
 */
export function decimalToNumberOr(
  d: Decimal | undefined,
  fallback = 0,
): number {
  return decimalToNumber(d) ?? fallback
}

export function formatDecimalValue(
  value: string | undefined,
  digits = 2,
): string {
  if (value === undefined || value === '') return '—'
  const num = parseFloat(value)
  if (isNaN(num)) return '—'
  return num.toFixed(digits)
}

export function formatNumber(value: number | undefined, digits = 1): string {
  return value !== undefined ? value.toFixed(digits) : '—'
}
